import {
    CheckoutState,
    RequestCheckoutState,
    OnRequestPricingOption,
    CourseDurationPricing,
} from '@shared/types';
import { PublicEducator, PublicOnRequestCourse } from '@cademy-package/sdk-component-types';
import { FunctionComponent, PropsWithChildren, createContext, useContext, useMemo } from 'react';
import { ResponseSubmitter } from '../../useCheckout';
import { CademyError } from '@shared/domain-shared';
import { CheckoutContext, CheckoutContextProvider } from '..';
import { usePaymentOptions } from '../../data/usePaymentOptions';

type RequestCheckoutContext = CheckoutContext<
    RequestCheckoutState,
    PublicOnRequestCourse,
    PublicEducator
> & {
    pricingOptions: OnRequestPricingOption;
    durationOptions: CourseDurationPricing[];
    educatorTravelSettings: PublicOnRequestCourse['educator_travel_settings'];
    studentTravelSettings: PublicOnRequestCourse['student_travel_settings'];
    selectedDuration: CourseDurationPricing | null;
    isCalendarBased: boolean;
    isInstantBooking: boolean;
    requiresPayment: boolean;
};

const RequestCheckoutContext = createContext<Error | RequestCheckoutContext>(
    new Error(
        'Attempted to useRequestCheckoutContext in a Component not wrapped in RequestCheckoutContextProvider'
    )
);

const DAY_IN_MINUTES = 24 * 60;

export type RequestCheckoutContextProviderProps = PropsWithChildren<{
    course: PublicOnRequestCourse;
    educator: PublicEducator;
    checkoutState: RequestCheckoutState;
    isLoading: boolean;
    submitResponse: ResponseSubmitter<RequestCheckoutState>;
    error: CademyError | null;
}>;
export const RequestCheckoutContextProvider: FunctionComponent<
    RequestCheckoutContextProviderProps
> = ({ course, educator, checkoutState, submitResponse, isLoading, error, children }) => {
    const selectedDuration = useMemo(() => {
        if (!checkoutState.stepState.delivery.state) {
            return null;
        }
        const { selectedDurationId } = checkoutState.stepState.delivery.state;
        const selectedDuration = course.duration_pricing.find(
            (pricing) => pricing._id === selectedDurationId
        );
        if (!selectedDuration) {
            return null;
        }
        return selectedDuration;
    }, [course, checkoutState]);

    const isCalendarBased = useMemo(() => {
        const canBookSlots =
            selectedDuration?.duration !== undefined && selectedDuration.duration <= DAY_IN_MINUTES;
        const hasBeenSkipped = checkoutState.stepState.dates.state?.skipAvailableDates === true;
        return (
            course.date_requests === 'calendar_based' &&
            canBookSlots === true &&
            hasBeenSkipped === false
        );
    }, [course, checkoutState, selectedDuration]);

    const isInstantBooking = useMemo(() => {
        return (
            (course.pricing_option.type === 'price-offers' ||
                course.pricing_option.type === 'free') &&
            isCalendarBased
        );
    }, [course, isCalendarBased]);

    const requiresPayment = useMemo(() => {
        return isInstantBooking && (checkoutState.stepState.delivery.state?.payableCost || 0) > 0;
    }, [isInstantBooking, checkoutState]);

    const { data: paymentOptions, isLoading: isLoadingPaymentOptions } = usePaymentOptions({
        courseId: checkoutState.courseId,
        educatorId: checkoutState.educatorId,
    });

    const orderContextValue = useMemo((): RequestCheckoutContext => {
        return {
            course,
            educator,
            checkoutState,
            submitResponse,
            isLoading: isLoading || isLoadingPaymentOptions,
            error,
            paymentOptions: paymentOptions || {
                card: { enabled: true },
                paypal: { enabled: false },
                invoice: { enabled: false },
                manual: { enabled: false },
                klarna: { enabled: false },
            },
            durationOptions: course.duration_pricing,
            pricingOptions: course.pricing_option,
            educatorTravelSettings: course.educator_travel_settings,
            studentTravelSettings: course.student_travel_settings,
            selectedDuration,
            isCalendarBased,
            isInstantBooking,
            requiresPayment,
        };
    }, [
        course,
        educator,
        checkoutState,
        submitResponse,
        isLoading,
        error,
        paymentOptions,
        isLoadingPaymentOptions,
        selectedDuration,
        isCalendarBased,
        isInstantBooking,
        requiresPayment,
    ]);

    const checkoutContextValue = useMemo((): CheckoutContext => {
        return {
            course,
            educator,
            checkoutState,
            submitResponse: submitResponse as ResponseSubmitter<CheckoutState>,
            isLoading: isLoading || isLoadingPaymentOptions,
            error,
            paymentOptions: paymentOptions || {
                card: { enabled: true },
                paypal: { enabled: false },
                invoice: { enabled: false },
                manual: { enabled: false },
                klarna: { enabled: false },
            },
        };
    }, [
        course,
        educator,
        checkoutState,
        submitResponse,
        isLoading,
        error,
        paymentOptions,
        isLoadingPaymentOptions,
    ]);

    return (
        <RequestCheckoutContext.Provider value={orderContextValue}>
            <CheckoutContextProvider value={checkoutContextValue}>
                {children}
            </CheckoutContextProvider>
        </RequestCheckoutContext.Provider>
    );
};

export const useRequestCheckoutContext = (): RequestCheckoutContext => {
    const context = useContext(RequestCheckoutContext);
    if (context instanceof Error) {
        throw context;
    }
    return context;
};
